// Main chat surface — header + scrolling message list + input. Loads the
// initial message history when the selected agent changes; from then on the
// store stays in sync via WebSocket events.
//
// All actions (Stop / Fork / Rename / Delete / Send) are funnelled through
// the chat header / input components, which own their own per-action
// loading state (edge case F1). The header gates input on agent status
// (edge case F3 lives in Composer).

import { useEffect, useState } from 'react';
import { useTranslation } from '../i18n';
import { useAppStore } from '../stores/app.store';
import {
  deleteAgentRequest,
  stopAgentRequest,
  forkAgent,
  renameAgentRequest,
  undeleteAgent,
} from '../lib/api';
import { useToastStore } from '../stores/toast.store';
import { sendAgentInput } from '../hooks/useSocket';
import { ChatHeader } from './ChatHeader';
import { Composer } from './organisms/Composer';
import { EmptyChatState } from './EmptyChatState';
import { ConversationList } from './ConversationList';
import { PlanModeBanner } from './PlanModeBanner';
import { RunnerUnavailableBanner } from './agents/RunnerUnavailableBanner';
import { SessionLostBanner } from './chat/SessionLostBanner';
import { SessionExpiredBanner } from './chat/SessionExpiredBanner';
import { ConversationPanel } from './founder/conversation-panel/ConversationPanel';
import { ConversationSearch } from './founder/conversation-search/ConversationSearch';

export interface ChatViewProps {
  onOpenSidebar?: () => void;
}

export function ChatView({ onOpenSidebar }: ChatViewProps = {}) {
  const { t } = useTranslation();
  const selectedAgentId = useAppStore((s) => s.selectedAgentId);
  const composerOpen = useAppStore((s) => s.composerOpen);
  const agent = useAppStore((s) =>
    s.selectedAgentId ? s.agents[s.selectedAgentId] : undefined,
  );
  const messages = useAppStore((s) =>
    s.selectedAgentId ? (s.messagesByAgent[s.selectedAgentId] ?? null) : null,
  );
  const loadMessages = useAppStore((s) => s.loadMessages);
  const upsertAgent = useAppStore((s) => s.upsertAgent);
  const selectAgent = useAppStore((s) => s.selectAgent);
  // Sprint 11b.3 (D-009 / OAC4) — `getPendingPromise(id)` returns the
  // in-flight `POST /api/agents/empty` promise for an optimistically-
  // created agent. `handleSend` awaits it before pushing the user's
  // first message so the send always lands on the real backend id.
  const getPendingPromise = useAppStore((s) => s.getPendingPromise);
  // Sprint 20.3 PR-6 → auto-failover-on-rate-limit (D013-EC-109) —
  // single banner state with two discriminated variants. Shown above
  // the composer when the most recent send failed with either
  // RUNNER_OFFLINE (offline variant, red palette) or RUNNER_RATE_LIMITED
  // (rate_limited variant, warning palette). The "Try another runner"
  // / "Move to another runner" button triggers
  // `POST /api/agents/:id/failover` for both variants — same backend
  // path, same `--resume` continuation.
  const runnerUnavailable = useAppStore((s) =>
    s.selectedAgentId ? s.runnerUnavailableByAgent[s.selectedAgentId] ?? null : null,
  );
  const setRunnerUnavailable = useAppStore((s) => s.setRunnerUnavailable);
  // Sprint 20.5 PR-2 (D013-EC-68) — SESSION_LOST latch for the
  // selected agent. When set, the SessionLostBanner renders above the
  // composer with a Retry CTA.
  const sessionLost = useAppStore((s) =>
    s.selectedAgentId ? s.sessionLostByAgent[s.selectedAgentId] ?? null : null,
  );
  // Sprint 20.6 PR-2 (D013-EC-72) — SESSION_EXPIRED latch. Renders
  // SessionExpiredBanner with a "Start a new agent" CTA. The two
  // banners are mutually exclusive in practice (a single agent.error
  // landing produces one or the other) but we render both blocks
  // independently so a future code that fires both surfaces both.
  const sessionExpired = useAppStore((s) =>
    s.selectedAgentId ? s.sessionExpiredByAgent[s.selectedAgentId] ?? null : null,
  );

  // Sprint messages-section-fixes (D-msg-1) — Edit on a user message
  // pre-fills the continue composer via this action. Selected at the
  // top of the component (before any conditional return) so the hook
  // order stays stable across renders.
  const injectComposerText = useAppStore((s) => s.injectComposerText);

  const [loadError, setLoadError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  // Sprint fc-conversation-search (§2.2, §5 SSoT audit item 1) — the
  // search row's own open state. Component-local, ephemeral, per
  // conversation (EC-4): closes on agent switch below rather than
  // carrying a stale query/result set into the next conversation.
  const [searchOpen, setSearchOpen] = useState(false);
  useEffect(() => {
    setSearchOpen(false);
  }, [selectedAgentId]);

  useEffect(() => {
    if (!selectedAgentId) return;
    // Sprint 11b.3 (D-009) — skip the `GET /api/agents/:id` round-trip
    // for optimistically-created rows whose backend id doesn't exist
    // yet. Once the create resolves, `selectedAgentId` swaps to the
    // real id and this effect re-runs with the right id (OAC4).
    if (selectedAgentId.startsWith('tmp-')) return;
    let cancelled = false;
    setLoadError(null);
    setLoading(true);
    loadMessages(selectedAgentId)
      .catch((err: unknown) => {
        if (cancelled) return;
        const message = err instanceof Error ? err.message : 'Load failed';
        setLoadError(message);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [selectedAgentId, loadMessages]);

  if (composerOpen) {
    return <Composer mode="new" onOpenSidebar={onOpenSidebar} />;
  }

  if (!selectedAgentId || !agent) {
    return (
      <main
        className="flex flex-col h-full w-full bg-card overflow-hidden"
        data-testid="chat-view"
      >
        <EmptyChatState onOpenSidebar={onOpenSidebar} />
      </main>
    );
  }

  // Sprint messages-section-fixes (D-msg-1) — Edit on a user message
  // hands the message text to the composer via
  // `pendingComposerInjection`. The Composer subscribes, consumes the
  // value once, and focuses the textarea. Real edit-via-resume against
  // the Claude Code adapter is deferred (spec §"Out of scope"); the
  // founder gets the prior text in the textarea ready to amend and
  // Send produces a fresh turn.
  const handleEditMessage = (text: string) => {
    injectComposerText(agent.id, text);
  };

  const handleSend = async (text: string, chipIds?: string[]) => {
    // OAC4 — the user pressed Enter while `POST /api/agents/empty` may
    // still be in flight. Await the create promise so the message
    // lands on the real backend id, not the `tmp-…` placeholder.
    let targetId = agent.id;
    const pending = getPendingPromise(agent.id);
    if (pending) {
      const real = await pending;
      targetId = real.id;
    }
    // Sprint 8 — chipIds (when present) are claimed by the backend at send
    // time; the message row carries the StoredAttachment[] back over the
    // websocket so the user bubble re-renders chips on every tab.
    try {
      await sendAgentInput(targetId, text, chipIds);
      // Sprint deploy-drain-warm-resume (D-A1 blocker-5, fence F7) — this
      // used to clear `runnerUnavailableByAgent` right here, on the claim
      // that "successful send means reachable". That claim is now FALSE:
      // the ack fires on `onAccepted` (message durable + queued into the
      // spawn pipeline), which happens BEFORE the destination runner has
      // confirmed the spawn — a resume can still fail after this promise
      // resolves. The clear now happens in `useSocket.ts`'s
      // `onAgentUpdated`, keyed off the `agent:updated` the backend emits
      // at the actual dispatch-confirmed moment (session-manager.ts,
      // same site as `clearFailoverWindow(agent.id, 'spawned-ack')`).
    } catch (err) {
      // Sprint 20.3 PR-6 → auto-failover-on-rate-limit (D013-EC-109).
      // Two error codes trigger the same failover banner with different
      // variants. The shared recovery is `POST /api/agents/:id/failover`
      // (resumes via `--resume <claudeSessionId>` on the picked runner).
      const code = (err as { code?: unknown } | null)?.code;
      const message = err instanceof Error ? err.message : 'Send failed';
      if (code === 'RUNNER_OFFLINE') {
        // Offline-shape error message includes the runnerId
        // ("Runner ${runnerId} is offline — reconnecting…"). Extract it
        // for display when present; otherwise fall back to the agent's
        // pinned runner_id so the banner always names the unreachable
        // host.
        const match = /Runner\s+(\S+)\s+is offline/i.exec(message);
        const fallback = useAppStore.getState().agents[targetId]?.runnerId ?? 'runner';
        const runnerId = match?.[1] ?? fallback;
        setRunnerUnavailable(targetId, runnerId, message, 'offline');
      } else if (code === 'RUNNER_RATE_LIMITED') {
        // Rate-limited-shape error message names the hostname (the
        // backend renders the founder-mode copy server-side). Display
        // pulls the runnerId directly from the agent row — the banner
        // copy receives `runner` as a substitution key and the i18n
        // bundle frames it (the runner id IS the user-facing name in
        // the absence of a separate label slice).
        const runnerId =
          useAppStore.getState().agents[targetId]?.runnerId ?? 'runner';
        setRunnerUnavailable(targetId, runnerId, message, 'rate_limited');
      }
      throw err;
    }
  };

  // Resend submits the message text as a brand-new turn — does NOT
  // edit the original message in place. The user bubble that triggered
  // Resend stays where it is; a duplicate is appended at the tail.
  const handleResendMessage = (text: string) => {
    void handleSend(text);
  };

  const handleStop = async () => {
    await stopAgentRequest(agent.id);
  };

  const handleFork = async () => {
    await forkAgent(agent.id);
  };

  const handleRename = async (name: string) => {
    const r = await renameAgentRequest(agent.id, name);
    upsertAgent(r.agent);
  };

  const handleDelete = async () => {
    const snapshot = agent;
    try {
      await deleteAgentRequest(agent.id);
    } catch {
      return;
    }
    selectAgent(null);
    useToastStore.getState().push({
      message: 'Agent deleted',
      action: {
        label: 'Undo',
        onClick: () => {
          upsertAgent(snapshot);
          selectAgent(snapshot.id);
          void undeleteAgent(snapshot.id)
            .then(upsertAgent)
            .catch(() => {
              useToastStore.getState().push({
                message:
                  'Undo failed — agent will be purged in a few seconds',
                durationMs: 8_000,
              });
            });
        },
      },
    });
  };

  // Sprint fc-conversation-search (§2.4, §6.2 row 10) — ChatView's own
  // ternary predicates, computed ONCE and handed down as declared props to
  // both ChatHeader (disables the trigger) and ConversationSearch (render
  // order states 1/2). Never re-derived inside either consumer (R1 B5,
  // Gate 2 major 3).
  const stillLoadingConversation = loading && messages === null;
  const conversationLoadFailed = Boolean(loadError) && messages === null;
  const searchDisabledReason = stillLoadingConversation
    ? t('conversationSearch.loading')
    : conversationLoadFailed
      ? t('conversationSearch.loadError')
      : undefined;

  return (
    <main
      className="flex flex-col h-full w-full bg-card overflow-hidden"
      data-testid="chat-view"
    >
      <ChatHeader
        agent={agent}
        onStop={handleStop}
        onFork={handleFork}
        onRename={handleRename}
        onDelete={handleDelete}
        onOpenSidebar={onOpenSidebar}
        onToggleSearch={() => setSearchOpen((v) => !v)}
        searchDisabled={stillLoadingConversation || conversationLoadFailed}
        searchDisabledReason={searchDisabledReason}
      />

      <PlanModeBanner agent={agent} />

      {/* Sprint fc-conversation-panel §1.3 — everything below ChatHeader
          and PlanModeBanner becomes the first column of a horizontal
          split; ConversationPanel is the second column, spanning the
          full height of this row (cursor-ux-mirror.md §1: the VS Code /
          Cursor secondary-sidebar translation). */}
      <div className="flex flex-row flex-1 min-h-0">
        <div className="flex flex-col flex-1 min-h-0 min-w-0 overflow-hidden">
          {searchOpen && (
            <ConversationSearch
              agentId={agent.id}
              agentName={agent.name}
              messages={messages}
              stillLoading={stillLoadingConversation}
              loadFailed={conversationLoadFailed}
              onClose={() => setSearchOpen(false)}
            />
          )}

          {stillLoadingConversation ? (
            <div
              className="flex-1 flex items-center justify-center text-body text-muted-foreground/70"
              data-testid="chat-loading"
            >
              {t('chatView.loadingMessages')}
            </div>
          ) : conversationLoadFailed ? (
            <div
              className="flex-1 flex items-center justify-center text-body text-status-error px-6 text-center"
              data-testid="chat-load-error"
            >
              {t('chatView.couldNotLoadMessages', { error: loadError ?? '' })}
            </div>
          ) : (
            <ConversationList
              agentId={agent.id}
              messages={messages ?? []}
              onEditMessage={handleEditMessage}
              onResendMessage={handleResendMessage}
            />
          )}

          {runnerUnavailable ? (
            <RunnerUnavailableBanner
              agentId={agent.id}
              runnerId={runnerUnavailable.runnerId}
              lastErrorMessage={runnerUnavailable.lastErrorMessage}
              variant={runnerUnavailable.variant}
            />
          ) : null}

          {sessionLost ? (
            <SessionLostBanner
              agentId={agent.id}
              message={sessionLost.message}
            />
          ) : null}

          {sessionExpired ? (
            <SessionExpiredBanner
              agentId={agent.id}
              message={sessionExpired.message}
            />
          ) : null}

          <Composer
            mode="continue"
            agent={agent}
            onSend={handleSend}
            onStop={handleStop}
          />
        </div>

        <ConversationPanel agentId={agent.id} />
      </div>
    </main>
  );
}
